import { format, useT } from "@/i18n";
import type { IconType } from "react-icons";
import { HiFire, HiOutlineTrophy } from "react-icons/hi2";
import {
  type AchievementTier,
  NEUTRAL_ACHIEVEMENT_FILL,
  getAchievementTier,
  getEarnedTier,
  getStreakAchievementProgress,
} from "./streakAchievements";

interface StreakCardProps {
  currentStreak: number;
  longestStreak: number;
  /** 0 until the student has completed a separate streak — gates the Best pill. */
  longestCompletedStreak: number;
}

/** Tier shows through background, border and icon only; the text stays ink at every tier. */
function Pill({
  icon: Icon,
  text,
  tier,
}: { icon: IconType; text: string; tier: AchievementTier | null }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] px-2.5 py-1 text-sm font-bold text-app-ink"
      style={{
        backgroundColor: tier?.background ?? "rgba(255, 255, 255, 0.95)",
        borderColor: tier?.accent ?? "transparent",
      }}
    >
      <Icon className="h-4 w-4 shrink-0" style={{ color: tier?.accent ?? "#2626A5" }} />
      {text}
    </span>
  );
}

/**
 * Streak and the climb to the next achievement tier. The pills answer different questions: the
 * flame is the live streak, the trophy is the all-time record, which a broken streak never takes
 * away — so the bar below tracks `currentStreak` only.
 */
export function StreakCard({
  currentStreak,
  longestStreak,
  longestCompletedStreak,
}: StreakCardProps) {
  const t = useT();
  const hasHistoricalBest = longestCompletedStreak > 0;
  const currentTier = getEarnedTier(currentStreak);
  const bestTier = getEarnedTier(longestStreak);
  const progress = getStreakAchievementProgress(currentStreak);
  const fillRatio = progress ? Math.min(currentStreak / progress.nextThreshold, 1) : 0;

  // "No streak" only when there is history to contradict — otherwise nothing has started yet.
  const streakText =
    currentStreak > 0
      ? format(t["report.streak.current"], { count: currentStreak })
      : hasHistoricalBest
        ? t["report.streak.none"]
        : t["report.streak.noneYet"];

  return (
    <div className="rounded-card bg-app-ink px-5 py-4 shadow-app-card">
      <p className="font-kumbh text-base font-bold text-white/85">{t["report.streak.header"]}</p>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <Pill
          icon={HiFire}
          text={streakText}
          tier={currentTier ? getAchievementTier(currentTier) : null}
        />
        {hasHistoricalBest && (
          <Pill
            icon={HiOutlineTrophy}
            text={format(
              longestStreak === 1 ? t["report.streak.bestOne"] : t["report.streak.bestOther"],
              { count: longestStreak }
            )}
            tier={bestTier ? getAchievementTier(bestTier) : null}
          />
        )}
      </div>

      {progress && (
        <div className="mt-3 space-y-1">
          <div className="flex items-center gap-2">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#AAAAFF]/30">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.round(fillRatio * 100)}%`,
                  backgroundColor: progress.highestEarnedTier
                    ? getAchievementTier(progress.highestEarnedTier).accent
                    : NEUTRAL_ACHIEVEMENT_FILL,
                }}
              />
            </div>
            <HiFire
              className="h-4 w-4 shrink-0"
              style={{ color: getAchievementTier(progress.nextTier).accent }}
            />
          </div>
          <p className="text-[10px] font-bold tracking-wide text-[#DFDFDF]">
            {format(t["report.streak.achievement"], {
              current: currentStreak,
              threshold: progress.nextThreshold,
            })}
          </p>
        </div>
      )}
    </div>
  );
}
